import React, { useState, useEffect } from 'react';
import { Settings, Euro, Key, Globe, Save, Loader2, CheckCircle, AlertCircle, ToggleLeft, ToggleRight, Wrench, Server, Send, Users, User, Shield, AlertTriangle, Mail, Trash2, Plus, Tv, ServerCog } from 'lucide-react';
import { motion } from 'framer-motion';
import CommissionSettings from '../../components/Admin/CommissionSettings';
import EmailsPage from './EmailsPage';
import IncidentModalPage from './IncidentModal';
import FraudPage from './FraudPage';
import PurgePage from './PurgePage';
import PricingPage from './PricingPage';
import GoldenOTTSettings from '../../components/Admin/GoldenOTTSettings';
import GoldenOTTAudit from '../../components/Admin/GoldenOTTAudit';
import api from '../../services/api';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('environment');
  const [envSettings, setEnvSettings] = useState({
    paypal_mode: 'sandbox',
    paypal_client_id: '',
    paypal_secret: '',
    paypal_sandbox_client_id: '',
    paypal_sandbox_secret: '',
    paypal_live_client_id: '',
    paypal_live_secret: '',
    mailgun_api_key: '',
    mailgun_domain: '',
    google_tag_manager_id: '',
    site_name: 'IPTV4KFR',
    site_title: 'IPTV4KFR | Une Offre Pour Toute la Famille',
    site_url: '',
    support_email: '',
    site_b_url: ''
  });
  const [maintenanceSettings, setMaintenanceSettings] = useState({
    enabled: false,
    message: 'Nous effectuons actuellement des travaux de maintenance pour améliorer votre expérience.',
    exit_password: ''
  });
  // DNS Settings
  const [dnsSettings, setDnsSettings] = useState({
    domains: [''],
    smarters_domain: '',
    m3u_output_type: 'mpegts'
  });
  const [dnsNotifyDomain, setDnsNotifyDomain] = useState('');
  const [clientsWithCredentials, setClientsWithCredentials] = useState([]);
  const [dnsNotifyTarget, setDnsNotifyTarget] = useState('all');
  const [dnsNotifyMessage, setDnsNotifyMessage] = useState('');
  const [sendingDnsNotification, setSendingDnsNotification] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (activeTab === 'environment') {
      fetchEnvSettings();
    } else if (activeTab === 'maintenance') {
      fetchMaintenanceSettings();
    } else if (activeTab === 'dns') {
      fetchDnsSettings();
      fetchClientsWithCredentials();
    }
  }, [activeTab]);

  const fetchEnvSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/environment');
      if (response.data) {
        const data = response.data;
        // Charger les clés selon le mode actuel
        if (data.paypal_mode === 'live') {
          data.paypal_client_id = data.paypal_live_client_id || '';
          data.paypal_secret = data.paypal_live_secret || '';
        } else {
          data.paypal_client_id = data.paypal_sandbox_client_id || '';
          data.paypal_secret = data.paypal_sandbox_secret || '';
        }
        setEnvSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.log('No environment settings found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/maintenance');
      if (response.data) {
        setMaintenanceSettings({
          enabled: response.data.enabled || false,
          message: response.data.message || 'Nous effectuons actuellement des travaux de maintenance pour améliorer votre expérience.',
          exit_password: response.data.exit_password || ''
        });
      }
    } catch (err) {
      console.log('No maintenance settings found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const fetchDnsSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/dns');
      if (response.data) {
        const domains = (response.data.domains && response.data.domains.length > 0)
          ? response.data.domains
          : (response.data.server_url ? [response.data.server_url] : ['']);
        setDnsSettings({
          domains,
          smarters_domain: response.data.smarters_domain || '',
          m3u_output_type: response.data.m3u_output_type || 'mpegts'
        });
        setDnsNotifyDomain(domains[0] || '');
      }
    } catch (err) {
      console.log('No DNS settings found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsWithCredentials = async () => {
    try {
      const response = await api.get('/admin/clients-with-credentials');
      setClientsWithCredentials(response.data || []);
    } catch (err) {
      console.log('Error fetching clients with credentials');
    }
  };

  const handleSaveDnsSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const cleanDomains = dnsSettings.domains.map(d => d.trim()).filter(Boolean);
      await api.post('/admin/settings/dns', { ...dnsSettings, domains: cleanDomains });
      setSuccess('Paramètres DNS enregistrés avec succès');
      setDnsNotifyDomain(prev => prev || cleanDomains[0] || '');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde DNS');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = () => setDnsSettings(prev => ({ ...prev, domains: [...prev.domains, ''] }));
  const removeDomain = (index) => setDnsSettings(prev => ({ ...prev, domains: prev.domains.filter((_, i) => i !== index) }));
  const updateDomain = (index, value) => setDnsSettings(prev => ({ ...prev, domains: prev.domains.map((d, i) => i === index ? value : d) }));

  const handleSendDnsNotification = async () => {
    if (!dnsNotifyDomain) {
      setError('Veuillez d\'abord sélectionner un domaine DNS');
      return;
    }
    
    setSendingDnsNotification(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post('/admin/dns/notify', {
        target: dnsNotifyTarget,
        server_url: dnsNotifyDomain,
        smarters_domain: dnsSettings.smarters_domain,
        message: dnsNotifyMessage
      });
      setSuccess(`Notification DNS envoyée à ${response.data.recipients_count} client(s)`);
      setDnsNotifyMessage('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de la notification');
    } finally {
      setSendingDnsNotification(false);
    }
  };

  const handleSaveEnvSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Sauvegarder les clés dans les bons champs selon le mode
      const dataToSave = { ...envSettings };
      if (dataToSave.paypal_mode === 'live') {
        dataToSave.paypal_live_client_id = dataToSave.paypal_client_id;
        dataToSave.paypal_live_secret = dataToSave.paypal_secret;
      } else {
        dataToSave.paypal_sandbox_client_id = dataToSave.paypal_client_id;
        dataToSave.paypal_sandbox_secret = dataToSave.paypal_secret;
      }
      
      await api.post('/admin/settings/environment', dataToSave);
      setSuccess('Paramètres enregistrés avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const togglePaypalMode = () => {
    setEnvSettings(prev => {
      const newMode = prev.paypal_mode === 'sandbox' ? 'live' : 'sandbox';
      
      // Charger les clés correspondant au nouveau mode
      let newClientId = '';
      let newSecret = '';
      
      if (newMode === 'live') {
        newClientId = prev.paypal_live_client_id || '';
        newSecret = prev.paypal_live_secret || '';
      } else {
        newClientId = prev.paypal_sandbox_client_id || '';
        newSecret = prev.paypal_sandbox_secret || '';
      }
      
      return {
        ...prev,
        paypal_mode: newMode,
        paypal_client_id: newClientId,
        paypal_secret: newSecret
      };
    });
  };

  const toggleMaintenanceMode = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const newEnabled = !maintenanceSettings.enabled;
      await api.post('/admin/settings/maintenance', {
        enabled: newEnabled,
        message: maintenanceSettings.message
      });
      setMaintenanceSettings(prev => ({ ...prev, enabled: newEnabled }));
      setSuccess(`Mode maintenance ${newEnabled ? 'activé' : 'désactivé'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const saveMaintenanceMessage = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/admin/settings/maintenance', maintenanceSettings);
      setSuccess('Message de maintenance enregistré');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-settings">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Paramètres</h1>
          <p className="text-[#B8B8BA] text-sm">Configuration du site, emails, sécurité et commissions</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#121214] p-1 rounded-lg border border-[#3A3A3D]/40 overflow-x-auto" data-testid="settings-tabs">
          {[
            { id: 'maintenance', icon: Wrench, label: 'Maintenance' },
            { id: 'dns', icon: Server, label: 'DNS' },
            { id: 'goldenott', icon: Tv, label: 'GoldenOTT' },
            { id: 'goldenott-audit', icon: ServerCog, label: 'Audit GoldenOTT' },
            { id: 'environment', icon: Globe, label: 'Environnement' },
            { id: 'commissions', icon: Euro, label: 'Commissions' },
            { id: 'emails', icon: Mail, label: 'Emails' },
            { id: 'incident', icon: AlertTriangle, label: 'Incident' },
            { id: 'fraud', icon: Shield, label: 'Anti-Fraude' },
            { id: 'purge', icon: Trash2, label: 'Purge Compte' },
            { id: 'pricing', icon: Euro, label: 'Tarifs' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === t.id ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>

        {activeTab === 'emails' ? (
          <EmailsPage />
        ) : activeTab === 'incident' ? (
          <IncidentModalPage />
        ) : activeTab === 'fraud' ? (
          <FraudPage />
        ) : activeTab === 'purge' ? (
          <PurgePage />
        ) : activeTab === 'pricing' ? (
          <PricingPage />
        ) : activeTab === 'goldenott' ? (
          <GoldenOTTSettings />
        ) : activeTab === 'goldenott-audit' ? (
          <GoldenOTTAudit />
        ) : (
        <>

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Messages */}
            {success && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-[#00FF88]" />
                <span className="text-[#00FF88]">{success}</span>
              </div>
            )}
            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />
                <span className="text-[#FF3B5C]">{error}</span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
              </div>
            ) : (
              <div className="max-w-2xl">
                {/* Toggle Maintenance */}
                <div className={`p-6 rounded-lg metallic-border ${maintenanceSettings.enabled ? 'bg-[#FF3B5C]/10 border-[#FF3B5C]' : 'bg-[#1A1A1D]'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                        <Wrench className={`h-6 w-6 ${maintenanceSettings.enabled ? 'text-[#FF3B5C]' : 'text-[#FF6B35]'}`} />
                        <span>Mode Maintenance</span>
                      </h3>
                      <p className="text-[#B8B8BA] text-sm mt-1">
                        {maintenanceSettings.enabled 
                          ? 'Le site est actuellement en maintenance. Seuls les admins peuvent y accéder.'
                          : 'Activez le mode maintenance pour bloquer l\'accès au site.'
                        }
                      </p>
                    </div>
                    <button
                      onClick={toggleMaintenanceMode}
                      disabled={saving}
                      className={`p-2 rounded-lg transition-colors ${
                        maintenanceSettings.enabled 
                          ? 'bg-[#FF3B5C] text-white hover:bg-[#FF2244]' 
                          : 'bg-[#3A3A3D] text-[#B8B8BA] hover:bg-[#4A4A4D]'
                      }`}
                    >
                      {maintenanceSettings.enabled ? (
                        <ToggleRight className="h-8 w-8" />
                      ) : (
                        <ToggleLeft className="h-8 w-8" />
                      )}
                    </button>
                  </div>

                  {/* Status Badge */}
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                    maintenanceSettings.enabled 
                      ? 'bg-[#FF3B5C] text-white' 
                      : 'bg-[#00FF88]/20 text-[#00FF88]'
                  }`}>
                    {maintenanceSettings.enabled ? '⚠️ MAINTENANCE ACTIVE' : '✓ Site en ligne'}
                  </div>
                </div>

                {/* Message personnalisé */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border mt-6">
                  <h3 className="text-lg font-bold text-white mb-4">Message de maintenance</h3>
                  <textarea
                    value={maintenanceSettings.message}
                    onChange={(e) => setMaintenanceSettings(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none min-h-[100px]"
                    placeholder="Message affiché aux visiteurs pendant la maintenance..."
                  />
                </div>

                {/* Mot de passe de sortie */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border mt-6">
                  <h3 className="text-lg font-bold text-white mb-2 flex items-center space-x-2">
                    <Key className="h-5 w-5 text-[#FFB800]" />
                    <span>Mot de passe de sortie</span>
                  </h3>
                  <p className="text-[#B8B8BA] text-sm mb-4">
                    Ce mot de passe permet de désactiver la maintenance depuis la page <code className="text-[#FF6B35]">/maintenance/exit</code>
                  </p>
                  <input
                    type="text"
                    value={maintenanceSettings.exit_password}
                    onChange={(e) => setMaintenanceSettings(prev => ({ ...prev, exit_password: e.target.value }))}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                    placeholder="Définir un mot de passe de sortie..."
                  />
                  <p className="text-[#6B6B6E] text-xs mt-2">
                    Utile si vous perdez l'accès à l'admin. Laissez vide pour désactiver cette option.
                  </p>
                </div>

                {/* Save button */}
                <button
                  onClick={saveMaintenanceMessage}
                  disabled={saving}
                  className="mt-6 btn-primary flex items-center space-x-2"
                >
                  {saving ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Save className="h-5 w-5" />
                  )}
                  <span>Enregistrer les paramètres</span>
                </button>

                {/* Warning */}
                <div className="bg-[#FFB800]/10 border border-[#FFB800] rounded-lg p-4 mt-6">
                  <p className="text-[#FFB800] text-sm">
                    <strong>⚠️ Attention :</strong> Quand le mode maintenance est activé, toutes les pages du site 
                    redirigent vers la page de maintenance. Seul l'accès à l'administration reste possible.
                  </p>
                  <p className="text-[#FFB800] text-sm mt-2">
                    <strong>🔗 Page de sortie :</strong> <code className="bg-[#FFB800]/20 px-2 py-0.5 rounded">/maintenance/exit</code>
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* DNS Tab */}
        {activeTab === 'dns' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Messages */}
            {success && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-[#00FF88]" />
                <span className="text-[#00FF88]">{success}</span>
              </div>
            )}
            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />
                <span className="text-[#FF3B5C]">{error}</span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#00FFFF]" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Configuration DNS globale */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Server className="h-6 w-6 text-[#00FFFF]" />
                    <span>Configuration DNS Globale</span>
                  </h3>
                  <p className="text-[#B8B8BA] text-sm mb-6">
                    Ces paramètres seront utilisés par défaut pour générer les liens M3U de tous les clients.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#E8E8E8] text-sm mb-2">URLs Serveur (DNS) *</label>
                      <p className="text-[#6B6B6E] text-xs mb-3">
                        Ajoutez un ou plusieurs domaines. Le premier est le domaine <strong>principal</strong> par défaut.
                      </p>
                      <div className="space-y-2">
                        {dnsSettings.domains.map((domain, index) => (
                          <div key={index} className="flex items-center gap-2" data-testid={`dns-domain-row-${index}`}>
                            <input
                              type="url"
                              value={domain}
                              onChange={(e) => updateDomain(index, e.target.value)}
                              className="flex-1 bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                              placeholder="http://dns.mondomaine.com"
                              data-testid={`dns-domain-input-${index}`}
                            />
                            {index === 0 && (
                              <span className="text-[#00FF88] text-xs whitespace-nowrap px-2">principal</span>
                            )}
                            {dnsSettings.domains.length > 1 && (
                              <button type="button" onClick={() => removeDomain(index)}
                                className="text-[#FF3B5C] hover:text-[#FF2244] p-2 rounded-lg hover:bg-[#FF3B5C]/10 transition-all shrink-0"
                                title="Supprimer ce domaine" data-testid={`dns-domain-remove-${index}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addDomain}
                        className="mt-3 flex items-center gap-2 text-[#00FFFF] hover:text-[#00E5E5] text-sm font-medium"
                        data-testid="dns-add-domain-btn">
                        <Plus className="h-4 w-4" /> Ajouter un domaine
                      </button>
                    </div>
                    
                    <div>
                      <label className="block text-[#E8E8E8] text-sm mb-2">Domaine IPTV Smarters (Samsung/LG)</label>
                      <input
                        type="url"
                        value={dnsSettings.smarters_domain}
                        onChange={(e) => setDnsSettings({...dnsSettings, smarters_domain: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none font-mono"
                        placeholder="http://dns.astrogol.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-[#E8E8E8] text-sm mb-2">Type de sortie M3U</label>
                      <select
                        value={dnsSettings.m3u_output_type}
                        onChange={(e) => setDnsSettings({...dnsSettings, m3u_output_type: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                      >
                        <option value="mpegts">mpegts (par défaut)</option>
                        <option value="ts">ts</option>
                        <option value="hls">hls</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Aperçu génération M3U */}
                  {dnsSettings.domains[0] && (
                    <div className="mt-6 p-4 bg-[#0A0A0B] rounded-lg border border-[#00FFFF]/30">
                      <p className="text-[#00FFFF] text-sm font-semibold mb-2">📋 Aperçu du lien M3U (domaine principal) :</p>
                      <code className="text-[#00FF88] text-xs break-all">
                        {dnsSettings.domains[0]}/get.php?username={"<USERNAME>"}&password={"<PASSWORD>"}&type=m3u_plus&output={dnsSettings.m3u_output_type}
                      </code>
                    </div>
                  )}
                  
                  <button
                    onClick={handleSaveDnsSettings}
                    disabled={saving}
                    className="mt-6 w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Save className="h-5 w-5" />
                    )}
                    <span>Enregistrer les paramètres DNS</span>
                  </button>
                </div>

                {/* Notification changement DNS */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Send className="h-6 w-6 text-[#FFB800]" />
                    <span>Notifier un changement DNS</span>
                  </h3>
                  <p className="text-[#B8B8BA] text-sm mb-6">
                    Envoyez un email à vos clients pour les informer d'un changement de serveur DNS.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Cible */}
                    <div>
                      <label className="block text-[#E8E8E8] text-sm mb-3">Destinataires</label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-[#222226] rounded-lg hover:bg-[#2A2A2E] transition-colors">
                          <input
                            type="radio"
                            name="dnsTarget"
                            value="all"
                            checked={dnsNotifyTarget === 'all'}
                            onChange={(e) => setDnsNotifyTarget(e.target.value)}
                            className="w-4 h-4 accent-[#00FFFF]"
                          />
                          <Users className="h-5 w-5 text-[#00FFFF]" />
                          <span className="text-white">Tous les clients ({clientsWithCredentials.length})</span>
                        </label>
                        
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-[#222226] rounded-lg hover:bg-[#2A2A2E] transition-colors">
                          <input
                            type="radio"
                            name="dnsTarget"
                            value="specific"
                            checked={dnsNotifyTarget !== 'all'}
                            onChange={() => setDnsNotifyTarget(clientsWithCredentials[0]?.id || 'all')}
                            className="w-4 h-4 accent-[#00FFFF]"
                          />
                          <User className="h-5 w-5 text-[#FF6B35]" />
                          <span className="text-white">Client spécifique</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Sélection client spécifique */}
                    {dnsNotifyTarget !== 'all' && (
                      <div>
                        <label className="block text-[#E8E8E8] text-sm mb-2">Sélectionner un client</label>
                        <select
                          value={dnsNotifyTarget}
                          onChange={(e) => setDnsNotifyTarget(e.target.value)}
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                        >
                          {clientsWithCredentials.map(client => (
                            <option key={client.id} value={client.id}>
                              {client.full_name} ({client.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Message personnalisé */}
                    <div>
                      <label className="block text-[#E8E8E8] text-sm mb-2">Message personnalisé (optionnel)</label>
                      <textarea
                        value={dnsNotifyMessage}
                        onChange={(e) => setDnsNotifyMessage(e.target.value)}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none min-h-[100px]"
                        placeholder="Ajoutez un message explicatif pour vos clients..."
                      />
                    </div>
                  </div>
                  
                  {/* Sélection du domaine à annoncer */}
                  <div className="mt-4">
                    <label className="block text-[#E8E8E8] text-sm mb-2">Domaine à annoncer</label>
                    <select
                      value={dnsNotifyDomain}
                      onChange={(e) => setDnsNotifyDomain(e.target.value)}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none font-mono"
                      data-testid="dns-notify-domain-select"
                    >
                      {dnsSettings.domains.map(d => d.trim()).filter(Boolean).map((d, i) => (
                        <option key={d} value={d}>{d}{i === 0 ? ' (principal)' : ''}</option>
                      ))}
                    </select>
                  </div>

                  {/* Warning */}
                  <div className="bg-[#FFB800]/10 border border-[#FFB800] rounded-lg p-4 mt-4">
                    <p className="text-[#FFB800] text-sm">
                      ⚠️ <strong>Attention :</strong> L'email contiendra automatiquement la nouvelle URL serveur 
                      ({dnsNotifyDomain || 'non définie'}) et le nouveau lien M3U généré pour chaque client.
                    </p>
                  </div>
                  
                  <button
                    onClick={handleSendDnsNotification}
                    disabled={sendingDnsNotification || !dnsNotifyDomain}
                    className="mt-4 w-full bg-[#FFB800] text-[#0A0A0B] py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-[#FFC400] transition-colors disabled:opacity-50"
                  >
                    {sendingDnsNotification ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Envoyer la notification DNS</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Environment Tab */}
        {activeTab === 'environment' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Messages */}
            {success && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-[#00FF88]" />
                <span className="text-[#00FF88]">{success}</span>
              </div>
            )}
            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />
                <span className="text-[#FF3B5C]">{error}</span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Site Settings */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                    <Settings className="h-5 w-5 text-[#FF6B35]" />
                    <span>Configuration du site</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Nom du site</label>
                      <input
                        type="text"
                        value={envSettings.site_name}
                        onChange={(e) => setEnvSettings({...envSettings, site_name: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="IPTV4KFR"
                      />
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Titre de page (onglet navigateur)</label>
                      <input
                        type="text"
                        value={envSettings.site_title}
                        onChange={(e) => setEnvSettings({...envSettings, site_title: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="IPTV4KFR | Une Offre Pour Toute la Famille"
                      />
                      <p className="text-[#6B6B6E] text-xs mt-2">Ce texte apparaît dans l'onglet du navigateur</p>
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">URL du site</label>
                      <input
                        type="url"
                        value={envSettings.site_url}
                        onChange={(e) => setEnvSettings({...envSettings, site_url: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="https://iptv4kfr.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Email support</label>
                      <input
                        type="email"
                        value={envSettings.support_email}
                        onChange={(e) => setEnvSettings({...envSettings, support_email: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="support@iptv4kfr.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">URL Site B (Paiement)</label>
                      <input
                        type="url"
                        value={envSettings.site_b_url}
                        onChange={(e) => setEnvSettings({...envSettings, site_b_url: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="https://pay.formapro-digital.com"
                      />
                      <p className="text-[#6B6B6E] text-xs mt-2">{"URL du site de paiement (Site B). Laissez vide pour utiliser le même domaine."}</p>
                    </div>
                  </div>
                </div>

                {/* PayPal Settings */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                    <Key className="h-5 w-5 text-[#FFB800]" />
                    <span>PayPal</span>
                  </h3>
                  <div className="space-y-4">
                    {/* Mode Toggle */}
                    <div className="flex items-center justify-between p-4 bg-[#222226] rounded-lg">
                      <div>
                        <span className="text-white font-medium">Mode</span>
                        <p className="text-[#6B6B6E] text-xs">Basculez entre test et production</p>
                      </div>
                      <button
                        onClick={togglePaypalMode}
                        className="flex items-center space-x-2"
                      >
                        {envSettings.paypal_mode === 'sandbox' ? (
                          <>
                            <ToggleLeft className="h-8 w-8 text-[#FFB800]" />
                            <span className="text-[#FFB800] font-bold">SANDBOX</span>
                          </>
                        ) : (
                          <>
                            <ToggleRight className="h-8 w-8 text-[#00FF88]" />
                            <span className="text-[#00FF88] font-bold">LIVE</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Client ID</label>
                      <input
                        type="text"
                        value={envSettings.paypal_client_id}
                        onChange={(e) => setEnvSettings({...envSettings, paypal_client_id: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none font-mono text-sm"
                        placeholder="AV..."
                      />
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Secret {((envSettings.paypal_mode === 'live' ? envSettings.paypal_live_secret_set : envSettings.paypal_sandbox_secret_set)) && <span className="text-[#00FF88] text-xs">(configuré — laisser vide pour conserver)</span>}</label>
                      <input
                        type="password"
                        value={envSettings.paypal_secret}
                        onChange={(e) => setEnvSettings({...envSettings, paypal_secret: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none font-mono text-sm"
                        placeholder={((envSettings.paypal_mode === 'live' ? envSettings.paypal_live_secret_set : envSettings.paypal_sandbox_secret_set)) ? '•••••••• (inchangé)' : 'Saisir le secret'}
                      />
                    </div>
                  </div>
                </div>

                {/* Mailgun Settings */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                    <Key className="h-5 w-5 text-[#FF6B9D]" />
                    <span>Mailgun</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">API Key {envSettings.mailgun_api_key_set && <span className="text-[#00FF88] text-xs">(configurée — laisser vide pour conserver)</span>}</label>
                      <input
                        type="password"
                        value={envSettings.mailgun_api_key}
                        onChange={(e) => setEnvSettings({...envSettings, mailgun_api_key: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none font-mono text-sm"
                        placeholder={envSettings.mailgun_api_key_set ? '•••••••• (inchangée)' : 'key-...'}
                      />
                    </div>
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Domaine</label>
                      <input
                        type="text"
                        value={envSettings.mailgun_domain}
                        onChange={(e) => setEnvSettings({...envSettings, mailgun_domain: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="mg.iptv4kfr.fr"
                      />
                    </div>
                  </div>
                </div>

                {/* Google Tag Manager */}
                <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                    <Key className="h-5 w-5 text-[#00FF88]" />
                    <span>Google Tag Manager</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#B8B8BA] text-sm mb-2">Container ID</label>
                      <input
                        type="text"
                        value={envSettings.google_tag_manager_id}
                        onChange={(e) => setEnvSettings({...envSettings, google_tag_manager_id: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                        placeholder="GTM-XXXXXX"
                      />
                    </div>
                    <p className="text-[#6B6B6E] text-xs">
                      Laissez vide pour désactiver le tracking
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveEnvSettings}
                disabled={saving}
                className="btn-primary px-8 flex items-center space-x-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Enregistrer les paramètres</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && <CommissionSettings />}
        </>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
